import { isEmpty, isNil } from 'lodash';
import { useContext, useEffect } from 'react';
import { AnchoredSheet, AnchoredSheetTitleBar } from '@shared/components/AnchoredSheet';
import { View } from '@shared/components/View';
import { MBButton } from '@shared/components/button';
import { MBLoadingIndicator } from '@shared/components/loading';
import { MBTextBody2 } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { i18n } from '@shared/util/i18n';
import { useCardExport } from '../hooks/CardExport';
import styles from './CardExport.module.css';

interface Props {
  anchorName: string;
  card: { id: string; workflowTemplateId: string | null };
  onCloseSheet(): void;
}

export function CardExport({ anchorName, card, onCloseSheet }: Props) {
  const { response, createCardExport } = useCardExport();
  const colors = useContext(ColorThemeContext);

  useEffect(() => {
    if (!response.isLoading && !isNil(response.url)) {
      openLink(response.url);
      onCloseSheet();
    }
  }, [response.isLoading, response.url, onCloseSheet]);

  useEffect(() => {
    createCardExport(card.id);
  }, [createCardExport, card.id]);

  const renderTitleBar = () => {
    return (
      <AnchoredSheetTitleBar
        rightIcon="exit"
        rightIconSize={grid(4)}
        onRightIconPress={onCloseSheet}
        title={i18n('common.download')}
      />
    );
  };

  function retry() {
    createCardExport(card.id);
  }

  function openLink(url: string) {
    window.open(url, 'coast-pdf-' + url);
  }

  return (
    <AnchoredSheet
      anchorName={anchorName}
      className={styles.cardExportSheet}
      initialSize="full"
      renderTitleBar={renderTitleBar}
      onClose={onCloseSheet}
      testID="download-card-sheet"
    >
      <View padding={2}>
        {response.isLoading && (
          <View padding={4}>
            <MBLoadingIndicator />
          </View>
        )}
        {!response.isLoading && !isEmpty(response.error) && (
          <>
            <View paddingHorizontal={2} paddingBottom={4} paddingTop={1}>
              <MBTextBody2 color={colors.error.foreground}>
                {i18n('errors.somethingWentWrong')}
              </MBTextBody2>
            </View>
            <MBButton theme="normal" title={i18n('common.retry')} onPress={retry} />
          </>
        )}
      </View>
    </AnchoredSheet>
  );
}
